import Link from 'next/link';
import { ResultListState } from '@coveo/headless';
import { SiteSearchResultRaw } from 'lib/types/coveo/SiteSearchResult';
import { SearchLocationFields } from '../LocationSearch.types';
import { buildSiteDetailUrl } from 'components/SiteMaster/utils';

type MapCoveoResultToCardProps = {
  results?: ResultListState;
  isLoading: boolean;
  totalCount?: number;
  fields?: SearchLocationFields;
};

type SiteCard = {
  id: string;
  name: string;
  url: string;
  city?: string;
  state?: string;
  address?: string;
  distanceMiles?: number;
  activeStudiesCount?: number;
  recruitingNowCount?: number;
};

export const mapCoveoResultToCard = (raw: SiteSearchResultRaw, fallbackId?: string): SiteCard => ({
  id:
    raw.identifiableuri ||
    fallbackId ||
    [raw.site_name, raw.site_address, raw.site_city, raw.site_statecode || raw.site_state]
      .filter(Boolean)
      .join('|'),
  name: raw.site_name,
  url: raw.site_url || buildSiteDetailUrl(raw.site_name),
  city: raw.site_city,
  state: raw.site_state || raw.site_statecode,
  address: raw.site_address,
  distanceMiles: raw.distance,
  activeStudiesCount: raw.site_activestudiescount,
  recruitingNowCount: raw.site_recruitingnowcount,
});

const MapCoveoResultToCard = ({
  results,
  isLoading,
  totalCount,
  fields,
}: MapCoveoResultToCardProps): JSX.Element => {
  const sites = (results?.results ?? []).map((result) =>
    mapCoveoResultToCard(result.raw as unknown as SiteSearchResultRaw, result.uniqueId)
  );
  const count = totalCount ?? sites.length;
  const heading = (fields?.sitesFoundText?.value || '{count} sites found').replace(
    '{count}',
    String(count)
  );
  const activeLabel = fields?.activeStudiesLabel?.value || 'Active Studies';
  const recruitingLabel = fields?.recruitingNowLabel?.value || 'Recruiting Now';
  const viewSiteLabel = fields?.viewSiteLabel?.value || 'View Site';

  if (isLoading) {
    return (
      <section
        id="location-search-results"
        aria-busy="true"
        aria-label="Location search results"
        style={{ padding: '24px 16px', maxWidth: 960, margin: '0 auto' }}
      >
        <p>Loading sites…</p>
      </section>
    );
  }

  if (!sites.length) {
    return (
      <section
        id="location-search-results"
        aria-label="Location search results"
        style={{ padding: '24px 16px', maxWidth: 960, margin: '0 auto' }}
      >
        <p>No sites found for this location.</p>
      </section>
    );
  }

  return (
    <section
      id="location-search-results"
      aria-label="Location search results"
      style={{ padding: '24px 16px', maxWidth: 960, margin: '0 auto' }}
    >
      <p style={{ marginBottom: 16, fontWeight: 600 }}>{heading}</p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
        {sites.map((site) => (
          <li
            key={site.id}
            style={{
              border: '1px solid #ccc',
              borderRadius: 4,
              padding: 16,
              background: '#fff',
            }}
          >
            <strong>{site.name}</strong>
            <div>{[site.city, site.state].filter(Boolean).join(', ')}</div>
            {site.address && <div>{site.address}</div>}
            {typeof site.distanceMiles === 'number' && (
              <div>{site.distanceMiles.toFixed(1)} mi</div>
            )}
            <div>
              {activeLabel}: {site.activeStudiesCount ?? 0}
            </div>
            <div>
              {recruitingLabel}: {site.recruitingNowCount ?? 0}
            </div>
            <Link href={site.url}>{viewSiteLabel}</Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default MapCoveoResultToCard;
